import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import DashboardStatCard from "../../components/dashboard/DashboardStatCard";
import { formatAmount } from "../../utils/formatMoney";
import {
  BoxIconLine,
  CalenderIcon,
  DollarLineIcon,
  GroupIcon,
  PageIcon,
  ShootingStarIcon,
  TableIcon,
  TaskIcon,
  UserCircleIcon,
} from "../../icons";

interface DashboardStats {
  totalBookings: number;
  activeDrivers: number;
  totalVehicles: number;
  pendingBookings: number;
  openTripIssues: number;
  averageRating: number;
  revenueThisMonth: number;
  deadLegsActive: number;
  flashSalesActive: number;
  totalReviews: number;
}

type StatSection = {
  title: string;
  cards: {
    label: string;
    value: string | number;
    link: string;
    icon: React.ReactNode;
    accent?: "brand" | "success" | "warning" | "error" | "neutral";
  }[];
};

function StatSkeleton() {
  return (
    <div className="animate-pulse rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
      <div className="h-12 w-12 rounded-xl bg-gray-200 dark:bg-gray-800" />
      <div className="mt-5 h-4 w-24 rounded bg-gray-200 dark:bg-gray-800" />
      <div className="mt-2 h-8 w-16 rounded bg-gray-200 dark:bg-gray-800" />
    </div>
  );
}

export default function TaxiDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axiosInstance
      .get("/dashboard/stats")
      .then((res) => setStats(res.data))
      .catch(() => setError("Failed to load dashboard stats"))
      .finally(() => setLoading(false));
  }, []);

  const sections: StatSection[] = stats
    ? [
        {
          title: "Bookings & revenue",
          cards: [
            {
              label: "Total bookings",
              value: stats.totalBookings,
              link: "/bookings",
              icon: <CalenderIcon />,
              accent: "brand",
            },
            {
              label: "Pending bookings",
              value: stats.pendingBookings,
              link: "/bookings",
              icon: <BoxIconLine />,
              accent: stats.pendingBookings > 0 ? "warning" : "neutral",
            },
            {
              label: "Revenue this month",
              value: formatAmount(stats.revenueThisMonth),
              link: "/statements",
              icon: <DollarLineIcon />,
              accent: "success",
            },
            {
              label: "Average rating",
              value: formatAmount(stats.averageRating, 1, ""),
              link: "/ride-reviews",
              icon: <ShootingStarIcon />,
              accent: "brand",
            },
          ],
        },
        {
          title: "Fleet",
          cards: [
            {
              label: "Active drivers",
              value: stats.activeDrivers,
              link: "/drivers",
              icon: <UserCircleIcon />,
              accent: "neutral",
            },
            {
              label: "Vehicles",
              value: stats.totalVehicles,
              link: "/vehicles",
              icon: <GroupIcon />,
              accent: "neutral",
            },
          ],
        },
        {
          title: "Support & promotions",
          cards: [
            {
              label: "Open trip issues",
              value: stats.openTripIssues,
              link: "/trip-issues",
              icon: <TaskIcon />,
              accent: stats.openTripIssues > 0 ? "error" : "neutral",
            },
            {
              label: "Total reviews",
              value: stats.totalReviews,
              link: "/ride-reviews",
              icon: <ShootingStarIcon />,
              accent: "neutral",
            },
            {
              label: "Active dead legs",
              value: stats.deadLegsActive,
              link: "/dead-legs",
              icon: <TableIcon />,
              accent: "neutral",
            },
            {
              label: "Flash sales",
              value: stats.flashSalesActive,
              link: "/flash-sales",
              icon: <PageIcon />,
              accent: "neutral",
            },
          ],
        },
      ]
    : [];

  const quickLinks = [
    { label: "New booking", to: "/bookings/add" },
    { label: "Booking log", to: "/bookings" },
    { label: "Price checker", to: "/price-checker" },
    { label: "Pause availability", to: "/service-pauses" },
  ];

  return (
    <div>
      <PageBreadcrumb pageTitle="Dashboard" />

      {error && (
        <div className="mb-6 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
          {error}
        </div>
      )}

      <div className="mb-8 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-brand-50 via-white to-white p-6 dark:border-gray-800 dark:from-brand-500/10 dark:via-gray-900 dark:to-gray-900 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-brand-600 dark:text-brand-400">
            Operations overview
          </p>
          <h2 className="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">
            Welcome back
          </h2>
          <p className="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
            Bookings, fleet, revenue, and promotions at a glance. Select a metric to open the
            related section.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {quickLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-brand-300 hover:text-brand-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-brand-500/40 dark:hover:text-brand-400"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-8">
          <div>
            <div className="mb-4 h-5 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 md:gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <StatSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {sections.map((section) => (
            <section key={section.title}>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {section.title}
              </h3>
              <div
                className={`grid grid-cols-1 gap-4 md:gap-6 ${
                  section.cards.length >= 4
                    ? "sm:grid-cols-2 xl:grid-cols-4"
                    : "sm:grid-cols-2"
                }`}
              >
                {section.cards.map((card) => (
                  <DashboardStatCard
                    key={card.label}
                    label={card.label}
                    value={card.value}
                    link={card.link}
                    icon={card.icon}
                    accent={card.accent}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
